import React, { useEffect, useState } from 'react';
import { Alert, alpha, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { apiGet } from 'api/service';
import { GREEN, MONO } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const INVOICE_COLOR = { paid: GREEN, issued: '#60a5fa', overdue: '#ef4444', void: '#64748b', draft: '#f59e0b' };

// What each limit means for a person, in the order they are likely to hit them.
const METERS = [
  ['scan_tokens_monthly', 'max_scan_tokens_monthly', 'Scan points this month', 'Bigger scans cost more points'],
  ['hosts_monthly', 'max_hosts_monthly', 'Hosts scanned this month', 'Each address scanned counts once per scan'],
  ['scans_daily', 'max_scans_daily', 'Scans in the last 24 hours', ''],
  ['running_scans', 'max_concurrent_scans', 'Scans running now', 'More wait in line'],
  ['targets', 'max_targets', 'Websites', 'Websites and addresses you added']
];
const meterState = (used, max) => {
  const pct = max ? Math.min(100, Math.round(((Number(used) || 0) / Number(max)) * 100)) : 0;
  return { pct, color: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : GREEN };
};

function Meter({ label, note, used, max }) {
  const { pct, color } = meterState(used, max);
  return (
    <Box sx={{ ...panel, p: 2.4, flex: '1 1 260px' }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 30, fontWeight: 800, fontFamily: MONO, lineHeight: 1.2 }}>
        {used || 0}<Typography component="span" color="text.secondary" sx={{ fontSize: 15, fontFamily: MONO }}> / {max || 0}</Typography>
      </Typography>
      <Box sx={{ mt: 1.2, height: 8, borderRadius: 5, bgcolor: 'divider' }}><Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 5, bgcolor: color }} /></Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>{pct >= 100 ? 'Limit reached' : `${pct}% used`}{note ? ` · ${note}` : ''}</Typography>
    </Box>
  );
}

export default function BillingQuotaPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiGet('/api/billing/summary/').then(setData).catch((e) => setError(e?.message || 'Could not load your plan and usage.'));
  }, []);

  if (!data && !error) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  const quota = data?.quota || {};
  const limits = quota.limits || {};
  const usage = quota.usage || {};
  const invoices = data?.invoices || [];
  const near = METERS.filter(([u, m]) => meterState(usage[u], limits[m]).pct >= 90);
  const resets = quota.period_end ? new Date(`${quota.period_end}T00:00:00`).toLocaleDateString([], { month: 'long', day: 'numeric' }) : '';

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Plan and usage</Typography>
          <Typography color="text.secondary" variant="body2">What your plan allows, how much you have used, and your invoices</Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Pill color={GREEN}>{(quota.quota_name || 'Default plan').toUpperCase()}</Pill>
          {quota.organization_name && <Typography variant="body2" color="text.secondary">{quota.organization_name}</Typography>}
        </Stack>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {near.length > 0 && <Alert severity="warning">You are close to a limit: {near.map(([, , label]) => label.toLowerCase()).join(', ')}. Monthly limits start again{resets ? ` on ${resets}` : ' next month'}. Ask your administrator if you need more.</Alert>}

      {data && (
        <Stack direction="row" gap={2} flexWrap="wrap">
          {METERS.map(([u, m, label, note]) => <Meter key={u} label={label} note={note} used={usage[u]} max={limits[m]} />)}
        </Stack>
      )}
      {data && <Typography variant="body2" color="text.secondary">Largest file you can upload: {limits.max_upload_mb} MB.{resets ? ` Monthly counts start again on ${resets}.` : ''}</Typography>}

      {data && (
        <Box sx={{ ...panel, overflow: 'hidden' }}>
          <Box sx={{ p: 2.2 }}><Typography sx={{ fontWeight: 700 }}>Invoices</Typography></Box>
          {!invoices.length && <Typography color="text.secondary" variant="body2" sx={{ px: 2.8, py: 2.4, borderTop: 1, borderColor: 'divider' }}>No invoices yet.</Typography>}
          {invoices.map((i) => (
            <Stack key={i.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.4, borderTop: 1, borderColor: 'divider' }}>
              <Typography sx={{ flex: '1 1 160px', fontFamily: MONO, fontWeight: 600 }}>{i.invoice_ref || `Invoice ${i.id}`}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 120px' }}>{i.plan || ''}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 200px' }}>{i.period_start} to {i.period_end}</Typography>
              <Typography sx={{ width: 110, textAlign: 'right', fontFamily: MONO, fontWeight: 700 }}>{i.amount} {i.currency}</Typography>
              <Pill color={INVOICE_COLOR[i.status] || '#64748b'}>{String(i.status || 'draft').toUpperCase()}</Pill>
            </Stack>
          ))}
        </Box>
      )}
    </Stack>
  );
}
